// Umpiring Assignments tab — admin's picking board.
//
// Fixed weekend layout: four rows × three columns.
//   Row 1  Saturday · AM   →  Linbrook | CC-1 | CC-2
//   Row 2  Saturday · PM   →  Linbrook | CC-1 | CC-2
//   Row 3  Sunday   · AM   →  Linbrook | CC-1 | CC-2
//   Row 4  Sunday   · PM   →  Linbrook | CC-1 | CC-2
//
// Each cell is either a rich scoreboard-style match card (when a game
// is scheduled at that slot) or a dashed placeholder naming the ground.
// The View Flyer button opens the printable Assignments Flyer overlay.

import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck, MapPin, Clock, CheckCircle2, RotateCcw,
  Printer, Trophy, Gavel, ScrollText, Award, Sun, Moon, CalendarDays
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import { fmt12 } from "../shared";
import AssignmentsFlyer from "./AssignmentsFlyer";

function initials(u) {
  const f = (u.firstName || "").trim();
  const l = (u.lastName  || "").trim();
  return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "?";
}

const CARD_TONES = ["gold", "cyan", "green", "violet", "orange"];
function nomTone(u) {
  const seed = (u.firstName || "") + (u.lastName || "") + String(u.umpireId || 0);
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return CARD_TONES[h % CARD_TONES.length];
}

function splitDate(iso) {
  if (!iso) return { day: "—", dow: "", mon: "" };
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return { day: iso, dow: "", mon: "" };
  const dow = d.toLocaleDateString("en-US", { weekday: "short" });
  const mon = d.toLocaleDateString("en-US", { month: "short" });
  return { day: String(d.getDate()), dow, mon };
}

// Upcoming weekend's Saturday + Sunday, in the user's local time zone.
// Rules match Upcoming Assignments:
//   - Saturday → today is the Sat, tomorrow is the Sun
//   - Sunday   → yesterday was the Sat, today is the Sun
//   - Other    → next upcoming Sat + Sun
function upcomingWeekendIsos() {
  const now = new Date();
  const dow = now.getDay(); // 0 Sun … 6 Sat
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  let sat;
  if (dow === 6) sat = start;
  else if (dow === 0) sat = new Date(start.getTime() - 86400000);
  else { sat = new Date(start); sat.setDate(start.getDate() + (6 - dow)); }
  const sun = new Date(sat); sun.setDate(sat.getDate() + 1);
  const toIso = d => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  return { satIso: toIso(sat), sunIso: toIso(sun) };
}

// Noon cutoff. Times like "13:30", "9:00 AM", "9 AM" all normalise.
function isAM(time) {
  if (!time) return true; // no time → default to AM slot
  const s = String(time).trim().toUpperCase();
  if (s.includes("PM")) return false;
  if (s.includes("AM")) return true;
  const m = s.match(/^(\d{1,2})/);
  if (!m) return true;
  const h = parseInt(m[1], 10);
  return h < 12;
}

// Grid columns are literal grounds ("Linbrook", "CC-1", "CC-2") — not
// the coarser Linbrook/Clarion rate bucket used elsewhere.
function bucketGroundGrid(g) {
  if (!g) return null;
  const s = String(g).trim().toLowerCase().replace(/\s+/g, "");
  if (s.startsWith("linbrook")) return "Linbrook";
  if (s.startsWith("cc1") || s.startsWith("cc-1")) return "CC-1";
  if (s.startsWith("cc2") || s.startsWith("cc-2")) return "CC-2";
  return null;
}

const GROUND_COLS = ["Linbrook", "CC-1", "CC-2"];

export default function UmpiringAssignmentsTab() {
  const toast = useToast();
  const [matches,   setMatches]   = useState([]);
  const [nomsById,  setNomsById]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);
  const [showFlyer, setShowFlyer] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const rows = await api.listUpcomingMatches();
      setMatches(rows);
      const withNoms = rows.filter(m => m.nominatedCount + m.assignedCount > 0);
      const nominations = await Promise.all(
        withNoms.map(m => api.listMatchNominations(m.id).then(list => [m.id, list]))
      );
      setNomsById(Object.fromEntries(nominations));
    } catch (err) {
      toast.error(err.message || "Couldn't load assignments.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function setStatus(matchId, umpireId, status) {
    setBusy(true);
    try {
      await api.setMatchNominationStatus(matchId, umpireId, status);
      const [rows, list] = await Promise.all([
        api.listUpcomingMatches(),
        api.listMatchNominations(matchId)
      ]);
      setMatches(rows);
      setNomsById(m => ({ ...m, [matchId]: list }));
    } catch (err) {
      toast.error(err.message || "Couldn't update assignment.");
    } finally { setBusy(false); }
  }

  const { satIso, sunIso } = useMemo(upcomingWeekendIsos, []);

  const rows = useMemo(() => ([
    { key: "sat-am", label: "Saturday · AM Games", date: satIso, ampm: "AM", tone: "gold",   icon: Sun          },
    { key: "sat-pm", label: "Saturday · PM Games", date: satIso, ampm: "PM", tone: "orange", icon: CalendarDays },
    { key: "sun-am", label: "Sunday · AM Games",   date: sunIso, ampm: "AM", tone: "cyan",   icon: Sun          },
    { key: "sun-pm", label: "Sunday · PM Games",   date: sunIso, ampm: "PM", tone: "violet", icon: Moon         }
  ]), [satIso, sunIso]);

  // Bucket matches → row × column.  Keeping this as a Map<rowKey, Map<col, match[]>>
  // means we can flag stacked matches (rare: two games same ground/slot) rather than
  // silently dropping one.
  const grid = useMemo(() => {
    const g = {};
    for (const r of rows) {
      g[r.key] = { Linbrook: [], "CC-1": [], "CC-2": [] };
    }
    for (const m of matches) {
      const col = bucketGroundGrid(m.ground);
      if (!col) continue;
      const ampm = isAM(m.time) ? "AM" : "PM";
      const row = rows.find(r => r.date === m.date && r.ampm === ampm);
      if (!row) continue;
      g[row.key][col].push(m);
    }
    return g;
  }, [matches, rows]);

  const totals = useMemo(() => {
    const weekend = matches.filter(m => (m.date === satIso || m.date === sunIso) && bucketGroundGrid(m.ground));
    const withNoms  = weekend.filter(m => (nomsById[m.id] || []).length > 0).length;
    const fullyDone = weekend.filter(m => m.assignedCount >= 2).length;
    const partly    = weekend.filter(m => m.assignedCount > 0 && m.assignedCount < 2).length;
    return { withNoms, fullyDone, partly, total: weekend.length };
  }, [matches, nomsById, satIso, sunIso]);

  const anyAssigned = matches.some(m => m.assignedCount > 0);

  function renderCell(row, col) {
    const cellMatches = grid[row.key][col];
    if (cellMatches.length === 0) {
      return (
        <div key={col} className={`ump-emptySlot ump-emptySlot--${row.tone}`}>
          <span className="ump-emptySlot-ground">{col}</span>
          <span className="ump-emptySlot-note">No game scheduled</span>
        </div>
      );
    }
    return (
      <div key={col} className="ump-cellStack">
        {cellMatches.map(m => renderMatchCard(m))}
      </div>
    );
  }

  function renderMatchCard(m) {
    const noms      = nomsById[m.id] || [];
    const assigned  = noms.filter(n => n.status === "Assigned");
    const nominated = noms.filter(n => n.status === "Nominated");
    const canAssignMore = assigned.length < 2;
    const isLocked  = assigned.length >= 2;
    const { day, dow, mon } = splitDate(m.date);
    return (
      <article
        key={m.id}
        className={`ump-matchCard${isLocked ? " ump-matchCard--locked" : ""}`}
      >
        {isLocked && (
          <span className="ump-matchCard-ribbon">
            <Award size={11}/> Locked In
          </span>
        )}

        <header className="ump-matchCard-head">
          <div className="ump-matchCard-series">
            <Trophy size={11}/> {m.series}
          </div>
          <div className="ump-matchCard-datebox">
            <span className="ump-matchCard-dow">{dow}</span>
            <span className="ump-matchCard-day">{day}</span>
            <span className="ump-matchCard-mon">{mon}</span>
          </div>
        </header>

        <div className="ump-matchCard-teams">
          <span className="ump-matchCard-team">{m.teamOne}</span>
          <span className="ump-matchCard-vs">vs</span>
          <span className="ump-matchCard-team">{m.teamTwo}</span>
        </div>

        <div className="ump-matchCard-meta">
          {m.time && (
            <span><Clock size={12}/> {fmt12(m.time)} ET</span>
          )}
          <span><MapPin size={12}/> {m.ground}</span>
        </div>

        <section className="ump-matchCard-section">
          <header className="ump-matchCard-sectionHead">
            <ScrollText size={12}/>
            <span>Umpires</span>
            <span className={`ump-matchCard-progress${isLocked ? " ump-matchCard-progress--done" : ""}`}>
              {assigned.length}/2 confirmed
            </span>
          </header>
          <div className="ump-slots">
            {[0, 1].map(slot => {
              const u = assigned[slot];
              if (!u) return (
                <div key={slot} className="ump-slot ump-slot--empty">
                  <div className="ump-slot-avatar ump-slot-avatar--empty">?</div>
                  <div className="ump-slot-info">
                    <b>Slot {slot + 1}</b>
                    <small>Pick from nominees</small>
                  </div>
                </div>
              );
              return (
                <div key={slot} className="ump-slot ump-slot--filled">
                  <div className="ump-slot-avatar">{initials(u)}</div>
                  <div className="ump-slot-info">
                    <b>{u.firstName} {u.lastName || ""}</b>
                    <small>{u.teamName || "Umpire"}</small>
                  </div>
                  <button
                    type="button"
                    className="ump-slot-demote"
                    onClick={() => setStatus(m.id, u.umpireId, "Nominated")}
                    disabled={busy}
                    title="Move back to nominees"
                    aria-label="Unassign umpire"
                  >
                    <RotateCcw size={12}/>
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="ump-matchCard-section">
          <header className="ump-matchCard-sectionHead">
            <Gavel size={12}/>
            <span>Nominees</span>
            <span className="ump-matchCard-progress">
              {nominated.length} available
            </span>
          </header>

          {nominated.length === 0 ? (
            <p className="ump-nominees-empty">
              {isLocked
                ? "Both umpires confirmed — no more picks needed."
                : "No pending nominations yet."}
            </p>
          ) : (
            <ul className="ump-nominees">
              {nominated.map(n => (
                <li key={n.id} className={`ump-nominee ump-nominee--${nomTone(n)}`}>
                  <div className="ump-nominee-avatar">{initials(n)}</div>
                  <div className="ump-nominee-info">
                    <b>{n.firstName} {n.lastName || ""}</b>
                    {n.teamName && <small>{n.teamName}</small>}
                  </div>
                  <button
                    type="button"
                    className="ump-nominee-btn"
                    onClick={() => setStatus(m.id, n.umpireId, "Assigned")}
                    disabled={busy || !canAssignMore}
                    title={canAssignMore
                      ? "Confirm this umpire"
                      : "Match is already at 2 umpires — unassign one first"}
                  >
                    <CheckCircle2 size={11}/> Assign
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </article>
    );
  }

  return (
    <>
      <Panel title={
        <div className="ump-assignBoard-heading">
          <ClipboardCheck size={18}/>
          <div>
            <span>Umpiring Assignments</span>
            <small>
              {totals.total} weekend match{totals.total === 1 ? "" : "es"} ·
              {" "}{totals.withNoms} with nominees ·
              {" "}{totals.fullyDone} locked in ·
              {" "}{totals.partly} pending
            </small>
          </div>
          <button
            type="button"
            className="ump-flyerBtn"
            onClick={() => setShowFlyer(true)}
            disabled={!anyAssigned}
            title={anyAssigned ? "View flyer" : "Assign umpires to view the flyer"}
          >
            <Printer size={13}/> View Flyer
          </button>
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading assignments…</p>}

        {!loading && (
          <div className="ump-assignGrid">
            {rows.map(r => {
              const Icon = r.icon;
              const { day, dow, mon } = splitDate(r.date);
              return (
                <section key={r.key} className={`ump-assignRow ump-assignRow--${r.tone}`}>
                  <header className={`ump-assignRow-head ump-assignRow-head--${r.tone}`}>
                    <span className="ump-assignRow-icon"><Icon size={22}/></span>
                    <div className="ump-assignRow-title">
                      <h3>{r.label}</h3>
                      <small>
                        {dow}, {mon} {day} · {r.ampm === "AM" ? "Morning slot (before 12 pm)" : "Afternoon slot (12 pm onward)"}
                      </small>
                    </div>
                    <span className="ump-assignRow-count">
                      {GROUND_COLS.reduce((n, c) => n + grid[r.key][c].length, 0)} game(s)
                    </span>
                  </header>

                  <div className="ump-assignRow-grid">
                    {GROUND_COLS.map(col => renderCell(r, col))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </Panel>

      {showFlyer && <AssignmentsFlyer onClose={() => setShowFlyer(false)}/>}
    </>
  );
}
